import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock, MessageSquare, Facebook, MessageCircle } from 'lucide-react';
import { personalInfo } from '../data/mock';
import { useInView } from '../hooks/useInView';

const ContactPage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [formRef, formInView] = useInView({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <>
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-cyan-400 to-teal-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-cyan-500/30">
              GET IN TOUCH
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-teal-300">Contact</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Interested in collaboration, research partnerships, or consulting services? I'd love to hear from you.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <section className="py-20 bg-white" ref={formRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className={`transition-all duration-700 ${formInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className="text-3xl lg:text-4xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-green-600">Connect</span>
              </h2>
              <p className="text-[#718096] mb-6 leading-relaxed">
                Whether you're interested in research collaboration, academic consultation, or institutional capacity building services, feel free to reach out.
              </p>

              {/* Contact Cards - Compact Grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-400 to-indigo-600 shadow-lg shadow-blue-500/20 hover:scale-105 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-white/70 text-[10px] font-medium">Email</p>
                    <p className="text-white font-bold text-xs truncate">dr.zaki@dr-zakibade.com</p>
                  </div>
                </a>

                <a
                  href={`tel:${personalInfo.phone}`}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-emerald-400 to-green-600 shadow-lg shadow-emerald-500/20 hover:scale-105 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">Phone</p>
                    <p className="text-white font-bold text-xs">{personalInfo.phone}</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/252634453948"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#25D366] to-[#128C7E] shadow-lg shadow-green-500/20 hover:scale-105 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">WhatsApp</p>
                    <p className="text-white font-bold text-xs">+252 63 4453 948</p>
                  </div>
                </a>

                <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 shadow-lg shadow-amber-500/20">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-medium">Location</p>
                    <p className="text-white font-bold text-xs">Hargeisa</p>
                  </div>
                </div>
              </div>

              {/* Social Links Row */}
              <div className="flex gap-3 mb-6">
                <a
                  href="https://www.facebook.com/saki.bade.5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1877F2] to-[#0D65D9] shadow-lg shadow-blue-500/20 hover:scale-105 transition-all duration-300"
                >
                  <Facebook className="w-5 h-5 text-white" />
                  <span className="text-white font-bold text-sm">Facebook</span>
                </a>
                <a
                  href="https://wa.me/252634453948"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#25D366] to-[#128C7E] shadow-lg shadow-green-500/20 hover:scale-105 transition-all duration-300"
                >
                  <MessageCircle className="w-5 h-5 text-white" />
                  <span className="text-white font-bold text-sm">WhatsApp</span>
                </a>
              </div>

              {/* Response Time - Compact */}
              <div className="rounded-xl p-4 bg-gradient-to-r from-purple-400 to-violet-600 text-white shadow-lg shadow-purple-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="font-bold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>Response Time</span>
                    <p className="text-white/80 text-xs">Usually within 24-48 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form - Professional Design */}
            <div className={`transition-all duration-700 delay-200 ${formInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative bg-gradient-to-br from-[#1F3C88] to-[#2A4FA8] rounded-3xl p-8 shadow-2xl shadow-[#1F3C88]/30 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-400/20 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-xl shadow-amber-500/30">
                      <MessageSquare className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>Send a Message</h3>
                      <p className="text-white/60 text-sm">I'll get back to you soon</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="group">
                        <label htmlFor="name" className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                          Full Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/20 focus:border-amber-400 focus:bg-white/20 outline-none transition-all font-medium text-white placeholder-white/40"
                            placeholder="Cabdilaahi Maxamed"
                          />
                        </div>
                      </div>
                      <div className="group">
                        <label htmlFor="email" className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                          Email Address
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/20 focus:border-amber-400 focus:bg-white/20 outline-none transition-all font-medium text-white placeholder-white/40"
                            placeholder="cabdilaahi@tusaale.com"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/20 focus:border-amber-400 focus:bg-white/20 outline-none transition-all font-medium text-white placeholder-white/40"
                        placeholder="What is this regarding?"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-white/80 text-xs font-bold mb-2 uppercase tracking-wider">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3.5 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/20 focus:border-amber-400 focus:bg-white/20 outline-none transition-all font-medium text-white placeholder-white/40 resize-none"
                        placeholder="Tell me about your inquiry..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitted}
                      className={`w-full flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${isSubmitted ? 'bg-gradient-to-r from-emerald-400 to-green-600 text-white shadow-xl shadow-emerald-500/30' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-xl shadow-amber-500/30 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-2xl'}`}
                    >
                      {isSubmitted ? (
                        <>
                          <CheckCircle className="w-6 h-6" />
                          Message Sent Successfully!
                        </>
                      ) : (
                        <>
                          <Send className="w-6 h-6" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>

                  {/* Trust Badges */}
                  <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center gap-2 text-white/60 text-xs">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>Quick Response</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60 text-xs">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>100% Secure</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60 text-xs">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span>Professional Service</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;